import { concat, fn, get } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import type Store from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { dropTask, restartableTask, timeout } from 'ember-concurrency';
import PowerSelect from 'ember-power-select/components/power-select';
import { eq, gt, or } from 'ember-truth-helpers';
import ProfilePhoto from 'garaje/components/employees/profile-photo';
import LoadMore from 'garaje/components/load-more';
import SortableHeaderColumn from 'garaje/components/sortable-header-column';
import ConfirmationModal from 'garaje/components/ui/classic/confirmation-modal';
import LoadingSpinnerPulse from 'garaje/components/ui/classic/loading-spinner-pulse';
import SettingsPanel from 'garaje/components/ui/classic/settings-panel';
import UiSearch from 'garaje/components/ui-search';
import dateFormat from 'garaje/helpers/date-format';
import type MacAddressMappingModel from 'garaje/models/mac-address-mapping';
import clickOutside from 'garaje/modifiers/click-outside';
import type AjaxFetchService from 'garaje/services/ajax-fetch';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type StateService from 'garaje/services/state';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import urlBuilder from 'garaje/utils/url-builder';

import CsvUploadModal from './csv-upload-modal';
import MacAddressMappingsModal from './modal';

const PAGE_SIZE = 25;
const DATE_FORMAT_STR = 'MMM d, yyyy h:mm aa';
interface SourceOption {
  name: string;
  value: string;
}

const SOURCE_OPTIONS: SourceOption[] = [
  { name: 'All Sources', value: '' },
  { name: 'Aruba Central', value: 'aruba_central' },
  { name: 'Cisco Meraki', value: 'meraki' },
  { name: 'CSV', value: 'csv' },
  { name: 'Jamf', value: 'jamf' },
  { name: 'Manual', value: 'manual' },
];

const SOURCE_DISPLAY: Record<string, string> = Object.fromEntries(
  SOURCE_OPTIONS.filter((o) => o.value).map((o) => [o.value, o.name]),
);

function formatSource(source: string): string {
  return SOURCE_DISPLAY[source] ?? source;
}

interface MacAddressMappingsTableSignature {
  Args: {
    companyId: string;
    search: string;
    source: string;
    changeSearch: (search: string) => void;
    changeSource: (source: string) => void;
  };
}

export default class MacAddressMappingsTable extends Component<MacAddressMappingsTableSignature> {
  @service declare flashMessages: FlashMessagesService;
  @service declare store: Store;
  @service declare state: StateService;
  @service declare ajaxFetch: AjaxFetchService;

  @tracked entries: MacAddressMappingModel[] = [];
  @tracked totalCount = 0;
  @tracked companyMappingCount = 0;
  @tracked mappingInModal: MacAddressMappingModel | null = null;
  @tracked isCreating = false;
  @tracked mappingToDelete: MacAddressMappingModel | null = null;
  @tracked profilePictures: Record<string, string> = {};
  @tracked csvError = '';
  @tracked showCsvUploadModal = false;
  @tracked isSettingsPanelOpen = false;
  @tracked showAddMenu = false;
  @tracked sortField = 'last-synced-at';
  @tracked sortDirection: 'asc' | 'desc' = 'desc';

  sourceOptions = SOURCE_OPTIONS;
  page = 0;

  private hasLoadedOnce = false;

  get sortParam(): string {
    return this.sortDirection === 'desc' ? `-${this.sortField}` : this.sortField;
  }

  get searchQuery(): string {
    return this.args.search ?? '';
  }

  get selectedSourceOption(): SourceOption {
    const value = this.args.source ?? '';
    return this.sourceOptions.find((o) => o.value === value) ?? this.sourceOptions[0]!;
  }

  get sourceFilter(): string {
    return this.args.source ?? '';
  }

  get hasMorePages(): boolean {
    return this.entries.length < this.totalCount;
  }

  resetTable(): void {
    this.page = 0;
  }

  loadData = restartableTask(async () => {
    try {
      const isFirstPage = this.page === 0;
      const nextPage = this.page + 1;
      const offset = (nextPage - 1) * PAGE_SIZE;

      const queryParams: Record<string, unknown> = {
        sort: this.sortParam,
        page: {
          offset,
          limit: PAGE_SIZE,
        },
      };

      if (this.sourceFilter) {
        queryParams['source'] = this.sourceFilter;
      }

      if (this.searchQuery) {
        queryParams['query'] = this.searchQuery;
      }

      const result = await this.store.query('mac-address-mapping', queryParams);
      const meta = (result as unknown as { meta?: { total?: number } }).meta;
      const newResults = result.slice();

      this.page = nextPage;

      // Replace entries on first page, append on subsequent pages
      this.entries = isFirstPage ? newResults : [...this.entries, ...newResults];

      // Use total from API meta if available
      if (meta?.total != null) {
        this.totalCount = meta.total;
        // Set company-wide count on first unfiltered load
        if (!this.hasLoadedOnce && !this.sourceFilter && !this.searchQuery) {
          this.companyMappingCount = meta.total;
          this.hasLoadedOnce = true;
        }
      } else if (newResults.length < PAGE_SIZE) {
        this.totalCount = this.entries.length;
      } else {
        this.totalCount = this.entries.length + 1;
      }

      // Load employee profile pictures for newly fetched entries
      const existingPictures = isFirstPage ? {} : this.profilePictures;
      const newEmployeeIds = [
        ...new Set(newResults.map((m) => m.employeeId).filter((id) => id && !existingPictures[String(id)])),
      ];
      if (newEmployeeIds.length > 0) {
        const pictures: Record<string, string> = { ...existingPictures };
        await Promise.all(
          newEmployeeIds.map(async (id) => {
            try {
              const employee = await this.store.findRecord('employee', String(id), {
                backgroundReload: false,
              });
              if (employee?.profilePictureUrl) {
                pictures[String(id)] = employee.profilePictureUrl;
              }
            } catch {
              // Employee not found or not accessible — use default avatar
            }
          }),
        );
        this.profilePictures = pictures;
      }
    } catch (error) {
      this.flashMessages.showAndHideFlash('error', 'Error loading MAC address mappings', {
        details: parseErrorForDisplay(error),
      });
      this.entries = [];
      this.totalCount = 0;
    }
  });

  searchTask = restartableTask(async () => {
    await timeout(300);
    this.resetTable();
    void this.loadData.perform();
  });

  deleteMapping = dropTask(async () => {
    const mapping = this.mappingToDelete;
    if (!mapping) return;

    try {
      const url = urlBuilder.macAddressMappings.delete();
      await this.ajaxFetch.request(url, {
        type: 'DELETE',
        contentType: 'application/json',
        data: JSON.stringify({
          companyId: parseInt(this.args.companyId, 10),
          macAddresses: [mapping.macAddress],
        }),
      });
      this.flashMessages.showAndHideFlash('success', 'Mapping deleted');
      this.companyMappingCount = Math.max(0, this.companyMappingCount - 1);
      this.mappingToDelete = null;
      this.resetTable();
      void this.loadData.perform();
    } catch (error) {
      this.flashMessages.showAndHideFlash('error', 'Error deleting mapping', {
        details: parseErrorForDisplay(error),
      });
    }
  });

  saveMapping = dropTask(
    async (data: { macAddress: string; email: string; employeeId?: string; employeeName?: string }) => {
      try {
        const isEditing = this.mappingInModal !== null;
        const mapping: Record<string, unknown> = {
          macAddress: data.macAddress,
          email: data.email,
          source: 'manual',
        };
        if (data.employeeId) mapping['employeeId'] = parseInt(data.employeeId, 10);
        if (data.employeeName) mapping['name'] = data.employeeName;

        if (isEditing) {
          const originalMac = this.mappingInModal!.macAddress;
          const url = urlBuilder.macAddressMappings.getByMacAddress(originalMac);
          await this.ajaxFetch.request(url, {
            type: 'PATCH',
            contentType: 'application/json',
            data: JSON.stringify({ ...mapping, companyId: parseInt(this.args.companyId, 10) }),
          });
        } else {
          const url = urlBuilder.macAddressMappings.sync();
          await this.ajaxFetch.request(url, {
            type: 'POST',
            contentType: 'application/json',
            data: JSON.stringify({
              companyId: parseInt(this.args.companyId, 10),
              source: 'manual',
              mappings: [mapping],
            }),
          });
        }
        this.flashMessages.showAndHideFlash('success', 'Mapping saved');
        if (!isEditing) {
          this.companyMappingCount += 1;
        }
        this.mappingInModal = null;
        this.isCreating = false;
        this.resetTable();
        void this.loadData.perform();
      } catch (error) {
        this.flashMessages.showAndHideFlash('error', 'Error saving mapping', {
          details: parseErrorForDisplay(error),
        });
      }
    },
  );

  @action
  onSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.args.changeSearch(target.value);
    void this.searchTask.perform();
  }

  @action
  onSearchClear(): void {
    this.args.changeSearch('');
    this.resetTable();
    void this.loadData.perform();
  }

  @action
  sortAction(field: string, direction: 'asc' | 'desc'): void {
    this.sortField = field;
    this.sortDirection = direction;
    this.resetTable();
    void this.loadData.perform();
  }

  @action
  onSourceFilterChange(option: SourceOption): void {
    this.args.changeSource(option.value);
    this.resetTable();
    void this.loadData.perform();
  }

  @action
  loadMore(): void {
    if (this.loadData.isRunning) return;
    if (this.hasMorePages) {
      void this.loadData.perform();
    }
  }

  @action
  openCreateModal(): void {
    this.mappingInModal = null;
    this.isCreating = true;
  }

  @action
  openEditModal(mapping: MacAddressMappingModel, event?: Event): void {
    event?.stopPropagation();
    this.mappingInModal = mapping;
    this.isCreating = false;
  }

  @action
  closeModal(): void {
    this.mappingInModal = null;
    this.isCreating = false;
  }

  @action
  openDeleteConfirmation(mapping: MacAddressMappingModel): void {
    this.mappingToDelete = mapping;
  }

  @action
  stopPropAndDelete(mapping: MacAddressMappingModel, event: Event): void {
    event.stopPropagation();
    this.mappingToDelete = mapping;
  }

  exportMappings = dropTask(async () => {
    try {
      const allMappings: MacAddressMappingModel[] = [];
      const exportPageSize = 1000;
      let offset = 0;
      let hasMore = true;

      while (hasMore) {
        const result = await this.store.query('mac-address-mapping', {
          page: { offset, limit: exportPageSize },
        });
        const page = result.slice();
        allMappings.push(...page);
        offset += exportPageSize;
        hasMore = page.length === exportPageSize;
      }

      const rows = ['email,mac_address'];
      for (const mapping of allMappings) {
        const email = mapping.employeeEmail ?? '';
        const mac = mapping.macAddress ?? '';
        rows.push(`${email},${mac}`);
      }

      const csv = rows.join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const anchor = document.createElement('a');
      anchor.download = 'mac-address-mappings.csv';
      anchor.href = URL.createObjectURL(blob);
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();

      this.flashMessages.showAndHideFlash('success', 'Export downloaded');
    } catch (error) {
      this.flashMessages.showAndHideFlash('error', 'Error exporting mappings', {
        details: parseErrorForDisplay(error),
      });
    }
  });

  uploadCsv = dropTask(async (file: File) => {
    this.showCsvUploadModal = false;

    try {
      const url = urlBuilder.macAddressMappings.syncCsv();
      const formData = new FormData();
      formData.append('companyId', this.args.companyId);
      formData.append('file', file);

      const response = await this.ajaxFetch.request(url, {
        type: 'POST',
        contentType: false,
        processData: false,
        data: formData,
      });

      const jobId = (response as { jobId: string }).jobId;
      if (jobId) {
        await this.pollCsvJob(jobId);
      } else {
        this.flashMessages.showAndHideFlash('success', 'CSV uploaded successfully');
        this.hasLoadedOnce = false;
        this.resetTable();
        void this.loadData.perform();
      }
    } catch (error) {
      this.flashMessages.showAndHideFlash('error', 'Error uploading CSV', {
        details: parseErrorForDisplay(error),
      });
    }
  });

  async pollCsvJob(jobId: string): Promise<void> {
    const url = urlBuilder.macAddressMappings.syncCsvJob(jobId);
    const maxAttempts = 60;

    for (let i = 0; i < maxAttempts; i++) {
      await timeout(2000);

      const job: { status: string; errorMessage?: string | null } = await this.ajaxFetch.request(url, {
        type: 'GET',
      });

      if (job.status === 'completed') {
        this.flashMessages.showAndHideFlash('success', 'CSV sync completed');
        this.hasLoadedOnce = false;
        this.resetTable();
        void this.loadData.perform();
        return;
      }

      if (job.status === 'failed') {
        this.flashMessages.showAndHideFlash('error', 'CSV sync failed', {
          details: job.errorMessage ?? 'Unknown error',
        });
        return;
      }
    }

    this.flashMessages.showAndHideFlash('warning', 'CSV sync is still processing. Please refresh later.');
  }

  @action
  toggleAddMenu(): void {
    this.showAddMenu = !this.showAddMenu;
  }

  @action
  closeAddMenu(): void {
    this.showAddMenu = false;
  }

  @action
  addSingleRecord(): void {
    this.showAddMenu = false;
    this.openCreateModal();
  }

  @action
  importFromCsv(): void {
    this.showAddMenu = false;
    this.showCsvUploadModal = true;
  }

  @action
  closeCsvUploadModal(): void {
    this.showCsvUploadModal = false;
  }

  @action
  showSettings(): void {
    this.isSettingsPanelOpen = true;
    if (!this.hasLoadedOnce) {
      void this.loadData.perform();
    }
  }

  @action
  hideSettings(): void {
    this.isSettingsPanelOpen = false;
  }

  @action
  closeDeleteConfirmation(): void {
    this.mappingToDelete = null;
  }

  <template>
    <SettingsPanel
      @isExpandable={{true}}
      @isOpen={{this.isSettingsPanelOpen}}
      @onCancel={{this.hideSettings}}
      data-test-mac-mappings-panel
      as |sp|
    >
      <sp.header
        @iconUri="/assets/images/features/directory.svg"
        @title="MAC address mappings"
        @description="Manage MAC address to employee mappings for presence detection."
        as |header|
      >
        <header.controls>
          {{#unless this.isSettingsPanelOpen}}
            <button
              type="button"
              class="btn btn--small btn--brand"
              data-test-mac-mappings-edit-btn
              {{on "click" this.showSettings}}
            >
              Edit
            </button>
          {{/unless}}
        </header.controls>
      </sp.header>
      <sp.body>
        <div class="mt-4">
          <div class="flex items-center justify-between pl-2 mb-4" data-test-mac-mappings-toolbar>
            <PowerSelect
              class="!mb-0"
              @dropdownClass="powerSelect-radio"
              @selected={{this.selectedSourceOption}}
              @options={{this.sourceOptions}}
              @searchEnabled={{false}}
              @onChange={{this.onSourceFilterChange}}
              data-test-mac-mappings-source-filter
              as |option|
            >
              {{option.name}}
            </PowerSelect>
            <div class="flex items-center gap-2">
              <button
                type="button"
                class="btn btn--outline btn--small"
                data-test-mac-mappings-export-btn
                disabled={{this.exportMappings.isRunning}}
                {{on "click" this.exportMappings.perform}}
              >
                {{if this.exportMappings.isRunning "Exporting..." "Export all"}}
              </button>
              <div class="relative" data-test-mac-mappings-add-dropdown {{clickOutside this.closeAddMenu}}>
                <button
                  type="button"
                  class="btn btn--brand btn--small !inline-flex items-center gap-1"
                  disabled={{this.uploadCsv.isRunning}}
                  data-test-mac-mappings-add-btn
                  {{on "click" this.toggleAddMenu}}
                >
                  {{#if this.uploadCsv.isRunning}}
                    <img src="/assets/images/progress-small-gray.svg" alt="" class="w-5 h-5 mr-1" />
                    Syncing...
                  {{else}}
                    Add mappings
                    <svg class="w-3 h-3 ml-1" viewBox="0 0 12 12" fill="currentColor"><path d="M2 4l4 4 4-4" /></svg>
                  {{/if}}
                </button>
                {{#if this.showAddMenu}}
                  <div
                    class="absolute right-0 mt-1 w-48 bg-white rounded-[8px] shadow-lg border border-carbon-10 z-10 py-1"
                    data-test-add-menu
                  >
                    <button
                      type="button"
                      class="w-full text-left px-4 py-2 text-sm hover:bg-gem-5 hover:text-gem-60"
                      data-test-add-single-record
                      {{on "click" this.addSingleRecord}}
                    >
                      Add a single mapping
                    </button>
                    <button
                      type="button"
                      class="w-full text-left px-4 py-2 text-sm hover:bg-gem-5 hover:text-gem-60"
                      disabled={{this.uploadCsv.isRunning}}
                      data-test-import-from-csv
                      {{on "click" this.importFromCsv}}
                    >
                      {{if this.uploadCsv.isRunning "Syncing..." "Import from CSV"}}
                    </button>
                  </div>
                {{/if}}
              </div>
            </div>
          </div>

          <div class="flex items-center w-full mb-4 pl-2">
            <div class="flex items-center gap-4 flex-auto mt-4">
              <span class="text-sm text-carbon-60">
                {{this.totalCount}}
                {{if (eq this.totalCount 1) "mapping" "mappings"}}{{#if this.searchQuery}}
                  matching "{{this.searchQuery}}"{{/if}}
              </span>
            </div>

            <UiSearch
              @placeholder="Search by name or MAC address"
              @onInput={{this.onSearchInput}}
              @value={{this.searchQuery}}
              @showClearButton={{if this.searchQuery true false}}
              @onClear={{this.onSearchClear}}
              style="width: 350px;"
              data-test-mac-mappings-search
            />
          </div>

          {{#if (gt this.entries.length 0)}}
            <div class="max-h-[600px] overflow-y-auto" data-test-mac-mappings-scroll-container>
              <div class="table w-full rounded text-carbon-50" data-test-mac-mappings-table>
                <div class="table-row">
                  <div
                    class="table-cell border-b bg-light-grey text-sm p-3 pl-5 px-4 align-middle font-medium whitespace-nowrap h-12"
                  >
                    <SortableHeaderColumn
                      @name="Employee name"
                      @sortField="employee-name"
                      @sortAction={{this.sortAction}}
                      @isActive={{eq this.sortField "employee-name"}}
                      @sortDirection={{this.sortDirection}}
                    />
                  </div>
                  <div
                    class="table-cell border-b bg-light-grey text-sm p-3 px-4 align-middle font-medium whitespace-nowrap h-12"
                  >
                    MAC address
                  </div>
                  <div
                    class="table-cell border-b bg-light-grey text-sm p-3 px-4 align-middle font-medium whitespace-nowrap h-12"
                  >
                    Source
                  </div>
                  <div
                    class="table-cell border-b bg-light-grey text-sm p-3 px-4 align-middle font-medium whitespace-nowrap h-12"
                  >
                    <SortableHeaderColumn
                      @name="Last synced"
                      @sortField="last-synced-at"
                      @sortAction={{this.sortAction}}
                      @isActive={{eq this.sortField "last-synced-at"}}
                      @sortDirection={{this.sortDirection}}
                    />
                  </div>
                  <div
                    class="table-cell border-b bg-light-grey text-sm p-3 px-4 align-middle font-medium whitespace-nowrap h-12"
                  >
                  </div>
                </div>

                {{#each this.entries as |mapping|}}
                  <div class="table-row hover:bg-carbon-5 h-16" data-test-mac-mapping-row>
                    <div
                      class="table-cell border-b p-3 pl-5 px-4 align-middle text-sm text-carbon-70"
                      data-test-employee-name
                    >
                      <div class="flex items-center gap-3">
                        <div class="uppercase shrink-0">
                          <ProfilePhoto
                            @id={{concat "" mapping.employeeId}}
                            @name={{mapping.employeeName}}
                            @devicePhoto={{get this.profilePictures (concat "" mapping.employeeId)}}
                            @isDisabled={{true}}
                            @size={{3}}
                          />
                        </div>
                        <LinkTo
                          @route="employees.directory.employee"
                          @model={{mapping.employeeId}}
                          class="font-medium text-carbon-70 hover:underline"
                        >
                          {{mapping.employeeName}}
                        </LinkTo>
                      </div>
                    </div>
                    <div
                      class="table-cell border-b p-3 px-4 align-middle text-sm font-mono text-carbon-70"
                      data-test-mac-address
                    >
                      {{mapping.macAddress}}
                    </div>
                    <div class="table-cell border-b p-3 px-4 align-middle text-sm text-carbon-50" data-test-source>
                      <span class="inline-block rounded bg-carbon-10 px-2 py-0.5 text-xs">
                        {{formatSource mapping.source}}
                      </span>
                    </div>
                    <div class="table-cell border-b p-3 px-4 align-middle text-sm text-carbon-50" data-test-last-synced>
                      {{#if mapping.lastSyncedAt}}
                        {{dateFormat mapping.lastSyncedAt DATE_FORMAT_STR}}
                      {{else}}
                        —
                      {{/if}}
                    </div>
                    <div class="table-cell border-b p-3 px-4 align-middle text-sm">
                      <div class="flex items-center gap-2">
                        <button
                          type="button"
                          class="w-6 h-6 hover:opacity-70 flex items-center justify-center"
                          title="Edit"
                          data-test-edit-mapping
                          {{on "click" (fn this.openEditModal mapping)}}
                        >
                          <img src="/assets/images/edit-16.svg" alt="edit" class="block w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          class="w-6 h-6 hover:opacity-70 flex items-center justify-center"
                          title="Delete"
                          data-test-delete-mapping
                          {{on "click" (fn this.stopPropAndDelete mapping)}}
                        >
                          <img src="/assets/images/trash-16.svg" alt="delete" class="block w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                {{/each}}
              </div>

              <LoadMore
                class="load-more m-4"
                @hasMorePages={{this.hasMorePages}}
                @useWindow={{false}}
                @action={{this.loadMore}}
              />
            </div>
          {{else if this.loadData.isRunning}}
            <LoadingSpinnerPulse data-test-loading-spinner class="pt-12" />
          {{else}}
            <div class="flex justify-center items-center py-12" data-test-empty-state>
              <div class="text-center text-base text-carbon-50">
                No MAC address mappings found
              </div>
            </div>
          {{/if}}
        </div>
      </sp.body>
    </SettingsPanel>

    {{#if (or this.isCreating this.mappingInModal)}}
      <MacAddressMappingsModal
        @mapping={{this.mappingInModal}}
        @onSave={{this.saveMapping.perform}}
        @onClose={{this.closeModal}}
        @isSaving={{this.saveMapping.isRunning}}
        @companyId={{@companyId}}
      />
    {{/if}}

    {{#if this.mappingToDelete}}
      <ConfirmationModal
        @title="Delete mapping"
        @ctaText="Delete"
        @handleTaskAction={{this.deleteMapping}}
        @onClose={{this.closeDeleteConfirmation}}
      >
        <p class="text-sm text-carbon-60">
          Are you sure you want to delete the mapping for:
        </p>
        <p class="mt-2 text-sm text-carbon-70">
          <strong>{{this.mappingToDelete.employeeName}}</strong>
          —
          <strong>{{this.mappingToDelete.macAddress}}</strong>
        </p>
      </ConfirmationModal>
    {{/if}}

    {{#if this.showCsvUploadModal}}
      <CsvUploadModal
        @onUpload={{this.uploadCsv.perform}}
        @onClose={{this.closeCsvUploadModal}}
        @isUploading={{this.uploadCsv.isRunning}}
        @currentMappingCount={{this.companyMappingCount}}
      />
    {{/if}}
  </template>
}
